import { useEffect, useRef } from "react";
import { motion } from "framer-motion";

/* ─── tiny animation preset ─── */
const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.12, duration: 0.65, ease: [0.4, 0, 0.2, 1] },
  }),
};

/* ─── Social icon SVGs ─── */
const FacebookIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
  </svg>
);
const YoutubeIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.6 12 3.6 12 3.6s-7.5 0-9.4.5A3 3 0 0 0 .5 6.2 31 31 0 0 0 0 12a31 31 0 0 0 .5 5.8 3 3 0 0 0 2.1 2.1c1.9.5 9.4.5 9.4.5s7.5 0 9.4-.5a3 3 0 0 0 2.1-2.1A31 31 0 0 0 24 12a31 31 0 0 0-.5-5.8zM9.6 15.6V8.4l6.3 3.6-6.3 3.6z" />
  </svg>
);
const LinkedInIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6zM2 9h4v12H2z" />
    <circle cx="4" cy="4" r="2" />
  </svg>
);
const InstagramIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
  </svg>
);

const SOCIALS = [
  { id: "ig", icon: <InstagramIcon />, href: "https://www.instagram.com/changeurstory.in/", label: "Instagram" },
  { id: "fb", icon: <FacebookIcon />, href: "https://www.facebook.com/ChangeUrStory.in/", label: "Facebook" },
  { id: "yt", icon: <YoutubeIcon />, href: "https://www.youtube.com/user/Chinmayeeabbey", label: "YouTube" },
  { id: "li", icon: <LinkedInIcon />, href: "https://www.linkedin.com/company/changeurstory/", label: "LinkedIn" },
];

export default function NiviLanding() {
  return (
    <div style={styles.root}>
      {/* ── decorative blobs ── */}
      <div style={{ ...styles.blob, ...styles.blobTopLeft }} />
      <div style={{ ...styles.blob, ...styles.blobTopRight }} />

      {/* ═══════════════════════════════
          HERO SECTION
      ═══════════════════════════════ */}
      <section style={styles.hero}>
        {/* Badge */}
        <motion.div
          initial="hidden" animate="visible" custom={0} variants={fadeUp}
          style={styles.badge}
        >
          <span style={styles.badgeFlame}>🔥</span>
          <span style={styles.badgeText}>Hybrid AI Interview Coach</span>
        </motion.div>

        {/* Headline */}
        <motion.h1
          initial="hidden" animate="visible" custom={1} variants={fadeUp}
          style={styles.headline}
        >
          Master Every<br />
          <span style={styles.headlineOrange}>Interview</span>
        </motion.h1>

        {/* Sub-copy */}
        <motion.p
          initial="hidden" animate="visible" custom={2} variants={fadeUp}
          style={styles.subCopy}
        >
          Turn anxiety into confidence. Our Hybrid AI Interview Coach simulates real interviews,
          provides instant feedback, and crafts perfect answers for any role.&nbsp;
          <strong>Go from nervous to hired in 7 days.</strong>
        </motion.p>

        {/* Stats row */}
        <motion.div
          initial="hidden" animate="visible" custom={3} variants={fadeUp}
          style={styles.statsRow}
        >
          <div style={styles.stat}>
            <img src="/image/user.png" alt="Professionals Helped" style={styles.statImg} />
            <span style={styles.statText}>2,000+ Professionals Helped</span>
          </div>
          <div style={styles.statDivider} />
          <div style={styles.stat}>
            <img src="/image/anakytics.png" alt="Got Promoted" style={styles.statImg} />
            <span style={styles.statText}>85% Got Promoted</span>
          </div>
        </motion.div>
      </section>

      {/* ═══════════════════════════════
          CHAT PREVIEW CARD
      ═══════════════════════════════ */}
      <motion.section
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.45, duration: 0.7, ease: [0.4, 0, 0.2, 1] }}
        style={styles.chatSection}
      >
        <div style={styles.chatCard}>
          {/* Frame 52.png - real wavy background image */}
          <img
            src="/image/Frame 52.png"
            alt=""
            aria-hidden="true"
            style={styles.waveBg}
          />

          {/* Nivi iframe */}
          <div style={styles.iframeWrapper}>
            <iframe
              src="https://nivichat.in/widget/496e0f1c-1f72-4cd2-8152-ca32166bd605"
              title="Nivi AI Interview Coach"
              style={styles.iframe}
              allow="microphone; camera"
            />
          </div>
        </div>
      </motion.section>

      {/* ═══════════════════════════════
          FOOTER
      ═══════════════════════════════ */}
      <footer style={styles.footer}>
        {SOCIALS.map((s) => (
          <a
            key={s.id}
            href={s.href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={s.label}
            style={styles.socialLink}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(249,115,22,0.12)";
              e.currentTarget.style.borderColor = "rgba(249,115,22,0.5)";
              e.currentTarget.style.color = "#f97316";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(255,255,255,0.7)";
              e.currentTarget.style.borderColor = "rgba(0,0,0,0.12)";
              e.currentTarget.style.color = "#64748b";
            }}
          >
            {s.icon}
          </a>
        ))}
      </footer>
    </div>
  );
}

/* ─────────────────────────────────────
   STYLES  (inline - no Tailwind clash)
───────────────────────────────────── */
const styles = {
  root: {
    minHeight: "100vh",
    background: "linear-gradient(160deg, #e4e4f5 0%, #eceaf8 18%, #eef0fb 38%, #f0edf8 58%, #f5f0e8 80%, #f8f0e8 100%)",
    fontFamily: "'Inter', 'Space Grotesk', sans-serif",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    position: "relative",
    overflow: "hidden",
  },

  /* ── blobs ── */
  blob: {
    position: "absolute",
    borderRadius: "50%",
    filter: "blur(70px)",
    pointerEvents: "none",
    zIndex: 0,
  },
  blobTopLeft: {
    width: 340,
    height: 280,
    top: -80,
    left: -80,
    background: "radial-gradient(circle, rgba(251,146,60,0.38) 0%, rgba(249,115,22,0.12) 60%, transparent 100%)",
  },
  blobTopRight: {
    width: 300,
    height: 260,
    top: -70,
    right: -60,
    background: "radial-gradient(circle, rgba(251,146,60,0.30) 0%, rgba(249,115,22,0.08) 60%, transparent 100%)",
  },

  /* ── hero ── */
  hero: {
    position: "relative",
    zIndex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    paddingTop: 72,
    paddingBottom: 32,
    paddingLeft: 24,
    paddingRight: 24,
    maxWidth: 720,
    width: "100%",
  },

  /* badge */
  badge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    border: "1.5px solid rgba(249,115,22,0.55)",
    borderRadius: 999,
    padding: "6px 16px",
    background: "rgba(249,115,22,0.07)",
    marginBottom: 28,
    cursor: "default",
  },
  badgeFlame: { fontSize: 13 },
  badgeText: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.08em",
    color: "#c2410c",
    textTransform: "uppercase",
  },

  /* headline */
  headline: {
    fontFamily: "'Space Grotesk', sans-serif",
    fontSize: "clamp(38px, 6vw, 64px)",
    fontWeight: 800,
    lineHeight: 1.12,
    color: "#1a1a2e",
    margin: "0 0 20px",
    letterSpacing: "-0.02em",
  },
  headlineOrange: {
    color: "#f97316",
    display: "inline-block",
  },

  /* sub-copy */
  subCopy: {
    fontSize: "clamp(15px, 2vw, 17px)",
    lineHeight: 1.7,
    color: "#475569",
    maxWidth: 540,
    margin: "0 0 32px",
  },

  /* stats */
  statsRow: {
    display: "flex",
    alignItems: "center",
    gap: 20,
    flexWrap: "wrap",
    justifyContent: "center",
  },
  stat: {
    display: "flex",
    alignItems: "center",
    gap: 7,
  },
  statImg: {
    width: 20,
    height: 20,
    objectFit: "contain",
    flexShrink: 0,
  },
  statText: {
    fontSize: 13,
    fontWeight: 600,
    color: "#334155",
    letterSpacing: "0.01em",
  },
  statDivider: {
    width: 1,
    height: 18,
    background: "rgba(0,0,0,0.15)",
  },

  /* ── chat card section ── */
  chatSection: {
    position: "relative",
    zIndex: 1,
    width: "100%",
    maxWidth: 920,
    padding: "0 20px 0",
  },
  chatCard: {
    position: "relative",
    borderRadius: 24,
    overflow: "hidden",
    background: "linear-gradient(145deg, #fdf6ef 0%, #fef3ea 40%, #fef8f2 100%)",
    boxShadow: "0 2px 40px 0 rgba(0,0,0,0.07), 0 0 0 1.5px rgba(0,0,0,0.06)",
    minHeight: 500,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "40px 20px",
  },

  /* Frame 52 background image */
  waveBg: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    objectPosition: "center",
    pointerEvents: "none",
    zIndex: 0,
    borderRadius: 24,
  },

  /* iframe wrapper */
  iframeWrapper: {
    position: "relative",
    zIndex: 1,
    borderRadius: 16,
    overflow: "hidden",
    boxShadow: "0 8px 40px rgba(0,0,0,0.13), 0 0 0 1px rgba(0,0,0,0.06)",
    width: 380,
    height: 440,
    background: "#fff",
    flexShrink: 0,
  },
  iframe: {
    width: "100%",
    height: "100%",
    border: "none",
    display: "block",
  },

  /* ── footer ── */
  footer: {
    position: "relative",
    zIndex: 1,
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "32px 24px 40px",
  },
  socialLink: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 40,
    height: 40,
    borderRadius: 10,
    border: "1.5px solid rgba(0,0,0,0.12)",
    background: "rgba(255,255,255,0.7)",
    color: "#64748b",
    textDecoration: "none",
    transition: "all 0.22s ease",
    cursor: "pointer",
  },
};
